// PERMANENT: Site-wide footer — includes AdSense-required legal links.
import { useCallback } from "react";
import { BookOpen, ExternalLink } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAVY = "#1B2F5E";
const GOLD = "#C9A96E";

export default function SiteFooter() {
  const location = useLocation();
  const navigate = useNavigate();

  const goToHash = useCallback((hash) => (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.pathname, navigate]);

  const linkStyle = { color: "rgba(255,255,255,0.75)", transition: "color 150ms ease" };
  const hoverIn = (e) => { e.currentTarget.style.color = GOLD; };
  const hoverOut = (e) => { e.currentTarget.style.color = "rgba(255,255,255,0.75)"; };

  return (
    <footer
      className="mt-24 sm:mt-32"
      style={{ background: NAVY, color: "#fff", borderTop: `1px solid ${GOLD}33` }}
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 pb-8 sm:pb-10 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: GOLD, color: NAVY }}
            >
              <BookOpen className="w-4 h-4" />
            </span>
            <span className="font-serif text-lg font-semibold text-white">
              Summary<span style={{ color: GOLD }}>Maps</span>
            </span>
          </div>
          <p className="text-sm leading-relaxed max-w-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
            Writing about visual learning, the books we map, and the science of remembering what you read.
          </p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>Explore</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#articles" onClick={goToHash("#articles")} style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-nav-articles">All articles</a></li>
            <li><a href="#how" onClick={goToHash("#how")} style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-nav-how">How it works</a></li>
            <li><Link to="/archive" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-nav-archive">Archive</Link></li>
            <li><a href="#newsletter" onClick={goToHash("#newsletter")} style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-nav-newsletter">Newsletter</a></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-about-link">About</Link></li>
            <li><Link to="/faq" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-faq-link">FAQ</Link></li>
            <li><Link to="/contact" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-contact-link">Contact</Link></li>
            <li><Link to="/privacy" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-privacy-link">Privacy Policy</Link></li>
            <li><Link to="/terms-of-service" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} data-testid="footer-terms-link">Terms of Service</Link></li>
            <li>
              <a
                href="https://summarymaps.com"
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
                onMouseEnter={hoverIn}
                onMouseLeave={hoverOut}
                className="inline-flex items-center gap-1.5"
                data-testid="footer-app-link"
              >
                summarymaps.com <ExternalLink className="w-3 h-3" />
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${GOLD}22` }}>
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs"
          style={{ color: "rgba(255,255,255,0.55)" }}
        >
          <span>© 2026 Summary Maps Journal. All rights reserved.</span>
          <span>Made for readers, not skimmers.</span>
        </div>
      </div>
    </footer>
  );
}
